import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from 'src/components/ui/input';
import { MapPin, Loader2, Search, X } from 'lucide-react';
import { searchAddress, resolveArcGisMagicKey } from 'src/config/services/fellas/google.api';

export interface PlaceSearchResult {
  name: string;
  full_address: string;
  coords: { lat: number; lng: number };
  city: string;
  province: string;
  address_components: any[];
  magicKey?: string;
}

interface LocationSearchInputProps {
  placeholder?: string;
  value?: string;
  currentCoords?: { lat: number; lng: number };
  onPlaceSelected: (place: PlaceSearchResult) => void;
  className?: string;
  dropdownDirection?: 'up' | 'down';
}

export const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  placeholder = 'Cari alamat lokasi...',
  value: initialValue = '',
  currentCoords,
  onPlaceSelected,
  className,
  dropdownDirection = 'up',
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<PlaceSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerSearch = useCallback(async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const results = await searchAddress(query, currentCoords);
      setSuggestions(results);
      setIsOpen(results.length > 0);
    } catch (err) {
      console.warn('Search error', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentCoords?.lat, currentCoords?.lng]);

  // Debounced typing search
  useEffect(() => {
    if (!inputValue || inputValue.trim().length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      triggerSearch(inputValue);
    }, 280);

    return () => clearTimeout(timer);
  }, [inputValue, triggerSearch]);

  const handleSelect = async (place: PlaceSearchResult) => {
    setIsLoading(true);
    try {
      if (place.coords && typeof place.coords.lat === 'number' && typeof place.coords.lng === 'number') {
        setInputValue(place.full_address || place.name);
        setIsOpen(false);
        onPlaceSelected(place);
      } else if (place.magicKey) {
        const resolved = await resolveArcGisMagicKey(place.magicKey, place.full_address);
        if (resolved) {
          setInputValue(resolved.full_address || resolved.name);
          setIsOpen(false);
          onPlaceSelected(resolved);
        }
      }
    } catch (err) {
      console.warn('Error resolving selected place', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        handleSelect(suggestions[0]);
      } else {
        triggerSearch(inputValue);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const dropdownPositionClasses = dropdownDirection === 'up'
    ? 'bottom-full mb-1.5'
    : 'top-full mt-1.5';

  return (
    <div ref={containerRef} className={`relative w-full ${className || ''}`}>
      <div className="relative flex items-center">
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!isOpen && e.target.value.trim().length >= 2) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className="pr-16 bg-white"
        />

        <div className="absolute right-1.5 flex items-center gap-1">
          {inputValue && (
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
              onClick={() => {
                setInputValue('');
                setSuggestions([]);
                setIsOpen(false);
              }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            type="button"
            className="p-1.5 text-gray-500 hover:text-primary hover:bg-pink-50 rounded-md transition-colors"
            onClick={() => triggerSearch(inputValue)}
            title="Cari alamat"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul
          className={`absolute left-0 right-0 z-[9999] ${dropdownPositionClasses} max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-2xl border border-gray-200 focus:outline-none`}
          style={{ zIndex: 9999 }}
        >
          {suggestions.map((item, idx) => (
            <li
              key={idx}
              className="relative cursor-pointer select-none py-2 px-3 hover:bg-pink-50 hover:text-primary transition-colors flex items-start gap-2.5 border-b last:border-b-0 border-gray-100"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item);
              }}
            >
              <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
              <div className="overflow-hidden">
                <p className="font-semibold text-gray-800 text-xs truncate">{item.name}</p>
                <p className="text-[11px] text-gray-500 truncate">{item.full_address}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationSearchInput;
